<template>
  <header
    class="sticky top-0 z-50 bg-background border-b border-border transition-colors duration-300"
  >
    <div class="px-2 md:px-8 flex justify-between items-center h-16">
      <!-- Left side: Logo or Left Pill -->
      <div
        :class="[
          'flex items-center gap-1 sm:gap-2 transition-all duration-300 min-w-0 flex-shrink',
          showBreadcrumbs
            ? 'bg-muted rounded-full p-1 sm:p-1.5 sm:pr-5 pr-2'
            : '',
        ]"
      >
        <div class="flex items-center space-x-2 pl-1">
          <router-link to="/home" class="flex items-center">
            <img src="/Logo-PG.svg" alt="Logo" class="h-10 w-auto" />
          </router-link>
          <button
            @click="toggleLogoDropdown"
            class="p-1 hover:bg-accent rounded transition-colors"
          >
            <svg
              class="w-5 h-5 text-muted-foreground"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </button>
        </div>

        <!-- Breadcrumbs (Visible when not on Home) -->
        <div v-if="showBreadcrumbs" class="flex items-center gap-2 ml-1">
          <!-- Category Pill (Conditionally Link/Span) -->
          <component
            :is="
              ['hubs', 'feed'].includes(currentCategory.type)
                ? 'div'
                : 'router-link'
            "
            :to="
              !['hubs', 'feed'].includes(currentCategory.type)
                ? currentCategory.path
                : null
            "
            class="flex items-center gap-2 bg-primary text-primary-foreground dark:bg-background dark:text-foreground px-2.5 sm:px-4 py-1.5 rounded-full text-[13px] font-bold whitespace-nowrap"
            :class="{
              'cursor-default': ['hubs', 'feed'].includes(currentCategory.type),
            }"
          >
            <svg
              v-if="currentCategory.type === 'learnings'"
              width="20"
              height="20"
              viewBox="0 0 20 20"
              xmlns="http://www.w3.org/2000/svg"
              class="fill-current"
            >
              <path
                d="M18.3333 4.04173V13.9501C18.3333 14.7584 17.675 15.5001 16.8667 15.6001L16.6083 15.6334C15.2417 15.8167 13.3167 16.3834 11.7667 17.0334C11.225 17.2584 10.625 16.8501 10.625 16.2584V4.66673C10.625 4.3584 10.8 4.07507 11.075 3.92507C12.6 3.10007 14.9083 2.36673 16.475 2.2334H16.525C17.525 2.2334 18.3333 3.04173 18.3333 4.04173Z"
              />
              <path
                d="M8.92682 3.92507C7.40182 3.10007 5.09349 2.36673 3.52682 2.2334H3.46849C2.46849 2.2334 1.66016 3.04173 1.66016 4.04173V13.9501C1.66016 14.7584 2.31849 15.5001 3.12682 15.6001L3.38516 15.6334C4.75182 15.8167 6.67682 16.3834 8.22682 17.0334C8.76849 17.2584 9.36849 16.8501 9.36849 16.2584V4.66673C9.36849 4.35006 9.20182 4.07507 8.92682 3.92507ZM4.16849 6.45006H6.04349C6.38516 6.45006 6.66849 6.7334 6.66849 7.07506C6.66849 7.42506 6.38516 7.70006 6.04349 7.70006H4.16849C3.82682 7.70006 3.54349 7.42506 3.54349 7.07506C3.54349 6.7334 3.82682 6.45006 4.16849 6.45006ZM6.66849 10.2001H4.16849C3.82682 10.2001 3.54349 9.92506 3.54349 9.57506C3.54349 9.2334 3.82682 8.95006 4.16849 8.95006H6.66849C7.01016 8.95006 7.29349 9.2334 7.29349 9.57506C7.29349 9.92506 7.01016 10.2001 6.66849 10.2001Z"
              />
            </svg>
            <svg
              v-else-if="currentCategory.type === 'hubs'"
              width="20"
              height="20"
              viewBox="0 0 20 20"
              xmlns="http://www.w3.org/2000/svg"
              class="fill-current"
            >
              <path
                d="M5 19.1667C4.30556 19.1667 3.71528 18.9236 3.22917 18.4375C2.74306 17.9514 2.5 17.3611 2.5 16.6667C2.5 15.9722 2.74306 15.3819 3.22917 14.8958C3.71528 14.4097 4.30556 14.1667 5 14.1667C5.19444 14.1667 5.375 14.1875 5.54167 14.2292C5.70833 14.2708 5.86806 14.3264 6.02083 14.3958L7.20833 12.9167C6.81944 12.4861 6.54861 12 6.39583 11.4583C6.24306 10.9167 6.20833 10.375 6.29167 9.83333L4.60417 9.27083C4.36806 9.61806 4.06944 9.89583 3.70833 10.1042C3.34722 10.3125 2.94444 10.4167 2.5 10.4167C1.80556 10.4167 1.21528 10.1736 0.729167 9.6875C0.243056 9.20139 0 8.61111 0 7.91667C0 7.22222 0.243056 6.63194 0.729167 6.14583C1.21528 5.65972 1.80556 5.41667 2.5 5.41667C3.19444 5.41667 3.78472 5.65972 4.27083 6.14583C4.75694 6.63194 5 7.22222 5 7.91667V8.08333L6.6875 8.66667C6.96528 8.16667 7.33694 7.74306 7.8025 7.39583C8.26805 7.04861 8.79222 6.82639 9.375 6.72917V4.91667C8.83333 4.76389 8.38556 4.46861 8.03167 4.03083C7.67778 3.59306 7.50056 3.08278 7.5 2.5C7.5 1.80556 7.74306 1.21528 8.22917 0.729167C8.71528 0.243056 9.30556 0 10 0C10.6944 0 11.2847 0.243056 11.7708 0.729167C12.2569 1.21528 12.5 1.80556 12.5 2.5C12.5 3.08333 12.3194 3.59389 11.9583 4.03167C11.5972 4.46944 11.1528 4.76444 10.625 4.91667V6.72917C11.2083 6.82639 11.7328 7.04861 12.1983 7.39583C12.6639 7.74306 13.0353 8.16667 13.3125 8.66667L15 8.08333V7.91667C15 7.22222 15.2431 6.63194 15.7292 6.14583C16.2153 5.65972 16.8056 5.41667 17.5 5.41667C18.1944 5.41667 18.7847 5.65972 19.2708 6.14583C19.7569 6.63194 20 7.22222 20 7.91667C20 8.61111 19.7569 9.20139 19.2708 9.6875C18.7847 10.1736 18.1944 10.4167 17.5 10.4167C17.0556 10.4167 16.6492 10.3125 16.2808 10.1042C15.9125 9.89583 15.6175 9.61806 15.3958 9.27083L13.7083 9.83333C13.7917 10.375 13.7569 10.9133 13.6042 11.4483C13.4514 11.9833 13.1806 12.4728 12.7917 12.9167L13.9792 14.375C14.1319 14.3056 14.2917 14.2536 14.4583 14.2192C14.625 14.1847 14.8056 14.1672 15 14.1667C15.6944 14.1667 16.2847 14.4097 16.7708 14.8958C17.2569 15.3819 17.5 15.9722 17.5 16.6667C17.5 17.3611 17.2569 17.9514 16.7708 18.4375C16.2847 18.9236 15.6944 19.1667 15 19.1667C14.3056 19.1667 13.7153 18.9236 13.2292 18.4375C12.7431 17.9514 12.5 17.3611 12.5 16.6667C12.5 16.3889 12.5453 16.1217 12.6358 15.865C12.7264 15.6083 12.8478 15.3756 13 15.1667L11.8125 13.6875C11.2431 14.0069 10.6356 14.1667 9.99 14.1667C9.34444 14.1667 8.73667 14.0069 8.16667 13.6875L7 15.1667C7.15278 15.375 7.27444 15.6078 7.365 15.865C7.45556 16.1222 7.50056 16.3894 7.5 16.6667C7.5 17.3611 7.25694 17.9514 6.77083 18.4375C6.28472 18.9236 5.69444 19.1667 5 19.1667Z"
              />
            </svg>
            <svg
              v-else-if="currentCategory.type === 'feed'"
              width="20"
              height="20"
              viewBox="0 0 20 20"
              xmlns="http://www.w3.org/2000/svg"
              class="fill-current"
            >
              <path
                d="M14.6089 6.47533C14.5505 6.46699 14.4922 6.46699 14.4339 6.47533C13.1422 6.43366 12.1172 5.37533 12.1172 4.07533C12.1172 2.75033 13.1922 1.66699 14.5255 1.66699C15.8505 1.66699 16.9339 2.74199 16.9339 4.07533C16.9255 5.37533 15.9005 6.43366 14.6089 6.47533Z"
              />
              <path
                d="M17.3237 12.2503C16.3904 12.8753 15.082 13.1087 13.8737 12.9503C14.1904 12.267 14.357 11.5087 14.3654 10.7087C14.3654 9.87534 14.182 9.08367 13.832 8.392C15.0654 8.22534 16.3737 8.45867 17.3154 9.08367C18.632 9.95034 18.632 11.3753 17.3237 12.2503Z"
              />
              <path
                d="M5.36745 6.47533C5.42578 6.46699 5.48411 6.46699 5.54245 6.47533C6.83411 6.43366 7.85912 5.37533 7.85912 4.07533C7.85912 2.74199 6.78411 1.66699 5.45078 1.66699C4.12578 1.66699 3.05078 2.74199 3.05078 4.07533C3.05078 5.37533 4.07578 6.43366 5.36745 6.47533Z"
              />
              <path
                d="M5.4599 10.7079C5.4599 11.5162 5.6349 12.2829 5.95156 12.9745C4.77656 13.0995 3.55156 12.8495 2.65156 12.2579C1.3349 11.3829 1.3349 9.95785 2.65156 9.08285C3.54323 8.48285 4.80156 8.24118 5.9849 8.37452C5.64323 9.07452 5.4599 9.86618 5.4599 10.7079Z"
              />
              <path
                d="M10.1 13.225C10.0333 13.2167 9.95833 13.2167 9.88333 13.225C8.35 13.175 7.125 11.9167 7.125 10.3667C7.13333 8.78333 8.40833 7.5 10 7.5C11.5833 7.5 12.8667 8.78333 12.8667 10.3667C12.8583 11.9167 11.6417 13.175 10.1 13.225Z"
              />
              <path
                d="M7.39297 14.9502C6.13464 15.7919 6.13464 17.1752 7.39297 18.0085C8.8263 18.9669 11.1763 18.9669 12.6096 18.0085C13.868 17.1669 13.868 15.7835 12.6096 14.9502C11.1846 13.9919 8.83464 13.9919 7.39297 14.9502Z"
              />
            </svg>
            <Gem
              v-else-if="currentCategory.type === 'pricing'"
              width="18"
              height="18"
              class="mr-0.5"
            />
            <svg
              v-else
              width="20"
              height="20"
              viewBox="0 0 20 20"
              xmlns="http://www.w3.org/2000/svg"
              class="fill-current"
            >
              <path
                fill="currentColor"
                d="M16.7001 5.68373L11.9001 2.3254C10.5918 1.40873 8.58345 1.45873 7.32511 2.43373L3.15011 5.69206C2.31678 6.34206 1.65845 7.6754 1.65845 8.7254V14.4754C1.65845 16.6004 3.38345 18.3337 5.50845 18.3337H14.4918C16.6168 18.3337 18.3418 16.6087 18.3418 14.4837V8.83373C18.3418 7.70873 17.6168 6.3254 16.7001 5.68373ZM10.6251 15.0004C10.6251 15.3421 10.3418 15.6254 10.0001 15.6254C9.65845 15.6254 9.37511 15.3421 9.37511 15.0004V12.5004C9.37511 12.1587 9.65845 11.8754 10.0001 11.8754C10.3418 11.8754 10.625 12.1587 10.625 12.5004V15.0004Z"
              />
            </svg>

            <span class="hidden sm:inline">{{ currentCategory.label }}</span>
          </component>
          <span
            v-if="hasSubpage"
            class="text-muted-foreground text-base font-bold ml-1"
            >»</span
          >
          <span
            v-if="hasSubpage"
            class="text-[14px] font-medium text-muted-foreground whitespace-nowrap overflow-hidden text-ellipsis max-w-[60px] xs:max-w-[80px] sm:max-w-[150px] md:max-w-[200px]"
          >
            {{ currentBreadcrumbTitle }}
          </span>
        </div>

        <!-- Logo Dropdown Menu -->
        <div
          v-if="showLogoDropdown"
          class="absolute top-16 left-8 mt-2 w-48 bg-card rounded-lg shadow-lg py-2 border border-border z-50 transition-all duration-200"
        >
          <router-link
            to="/home"
            class="flex items-center gap-3 px-4 py-2.5 text-sm font-medium text-foreground hover:bg-muted transition-colors"
            @click="showLogoDropdown = false"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              xmlns="http://www.w3.org/2000/svg"
              class="fill-current"
            >
              <path
                fill="currentColor"
                d="M16.7001 5.68373L11.9001 2.3254C10.5918 1.40873 8.58345 1.45873 7.32511 2.43373L3.15011 5.69206C2.31678 6.34206 1.65845 7.6754 1.65845 8.7254V14.4754C1.65845 16.6004 3.38345 18.3337 5.50845 18.3337H14.4918C16.6168 18.3337 18.3418 16.6087 18.3418 14.4837V8.83373C18.3418 7.70873 17.6168 6.3254 16.7001 5.68373ZM10.6251 15.0004C10.6251 15.3421 10.3418 15.6254 10.0001 15.6254C9.65845 15.6254 9.37511 15.3421 9.37511 15.0004V12.5004C9.37511 12.1587 9.65845 11.8754 10.0001 11.8754C10.3418 11.8754 10.625 12.1587 10.625 12.5004V15.0004Z"
              />
            </svg>
            Home
          </router-link>
          <router-link
            to="/learning-goals"
            class="flex items-center gap-3 px-4 py-2.5 text-sm font-medium text-foreground hover:bg-muted transition-colors"
            @click="showLogoDropdown = false"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              xmlns="http://www.w3.org/2000/svg"
              class="fill-current"
            >
              <path
                d="M18.3333 4.04173V13.9501C18.3333 14.7584 17.675 15.5001 16.8667 15.6001L16.6083 15.6334C15.2417 15.8167 13.3167 16.3834 11.7667 17.0334C11.225 17.2584 10.625 16.8501 10.625 16.2584V4.66673C10.625 4.3584 10.8 4.07507 11.075 3.92507C12.6 3.10007 14.9083 2.36673 16.475 2.2334H16.525C17.525 2.2334 18.3333 3.04173 18.3333 4.04173Z"
              />
              <path
                d="M8.92682 3.92507C7.40182 3.10007 5.09349 2.36673 3.52682 2.2334H3.46849C2.46849 2.2334 1.66016 3.04173 1.66016 4.04173V13.9501C1.66016 14.7584 2.31849 15.5001 3.12682 15.6001L3.38516 15.6334C4.75182 15.8167 6.67682 16.3834 8.22682 17.0334C8.76849 17.2584 9.36849 16.8501 9.36849 16.2584V4.66673C9.36849 4.35006 9.20182 4.07507 8.92682 3.92507ZM4.16849 6.45006H6.04349C6.38516 6.45006 6.66849 6.7334 6.66849 7.07506C6.66849 7.42506 6.38516 7.70006 6.04349 7.70006H4.16849C3.82682 7.70006 3.54349 7.42506 3.54349 7.07506C3.54349 6.7334 3.82682 6.45006 4.16849 6.45006ZM6.66849 10.2001H4.16849C3.82682 10.2001 3.54349 9.92506 3.54349 9.57506C3.54349 9.2334 3.82682 8.95006 4.16849 8.95006H6.66849C7.01016 8.95006 7.29349 9.2334 7.29349 9.57506C7.29349 9.92506 7.01016 10.2001 6.66849 10.2001Z"
              />
            </svg>
            Skill Path
          </router-link>
          <router-link
            to="/feed"
            class="flex items-center gap-3 px-4 py-2.5 text-sm font-medium text-foreground hover:bg-muted transition-colors"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              xmlns="http://www.w3.org/2000/svg"
              class="fill-current"
            >
              <path
                d="M14.6089 6.47533C14.5505 6.46699 14.4922 6.46699 14.4339 6.47533C13.1422 6.43366 12.1172 5.37533 12.1172 4.07533C12.1172 2.75033 13.1922 1.66699 14.5255 1.66699C15.8505 1.66699 16.9339 2.74199 16.9339 4.07533C16.9255 5.37533 15.9005 6.43366 14.6089 6.47533Z"
              />
              <path
                d="M17.3237 12.2503C16.3904 12.8753 15.082 13.1087 13.8737 12.9503C14.1904 12.267 14.357 11.5087 14.3654 10.7087C14.3654 9.87534 14.182 9.08367 13.832 8.392C15.0654 8.22534 16.3737 8.45867 17.3154 9.08367C18.632 9.95034 18.632 11.3753 17.3237 12.2503Z"
              />
              <path
                d="M5.36745 6.47533C5.42578 6.46699 5.48411 6.46699 5.54245 6.47533C6.83411 6.43366 7.85912 5.37533 7.85912 4.07533C7.85912 2.74199 6.78411 1.66699 5.45078 1.66699C4.12578 1.66699 3.05078 2.74199 3.05078 4.07533C3.05078 5.37533 4.07578 6.43366 5.36745 6.47533Z"
              />
              <path
                d="M5.4599 10.7079C5.4599 11.5162 5.6349 12.2829 5.95156 12.9745C4.77656 13.0995 3.55156 12.8495 2.65156 12.2579C1.3349 11.3829 1.3349 9.95785 2.65156 9.08285C3.54323 8.48285 4.80156 8.24118 5.9849 8.37452C5.64323 9.07452 5.4599 9.86618 5.4599 10.7079Z"
              />
              <path
                d="M10.1 13.225C10.0333 13.2167 9.95833 13.2167 9.88333 13.225C8.35 13.175 7.125 11.9167 7.125 10.3667C7.13333 8.78333 8.40833 7.5 10 7.5C11.5833 7.5 12.8667 8.78333 12.8667 10.3667C12.8583 11.9167 11.6417 13.175 10.1 13.225Z"
              />
              <path
                d="M7.39297 14.9502C6.13464 15.7919 6.13464 17.1752 7.39297 18.0085C8.8263 18.9669 11.1763 18.9669 12.6096 18.0085C13.868 17.1669 13.868 15.7835 12.6096 14.9502C11.1846 13.9919 8.83464 13.9919 7.39297 14.9502Z"
              />
            </svg>
            Feed
          </router-link>
          <div
            class="flex items-center gap-3 px-4 py-2.5 text-sm font-medium text-muted-foreground opacity-50 grayscale cursor-not-allowed select-none"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              xmlns="http://www.w3.org/2000/svg"
              class="fill-current"
            >
              <path
                d="M5 19.1667C4.30556 19.1667 3.71528 18.9236 3.22917 18.4375C2.74306 17.9514 2.5 17.3611 2.5 16.6667C2.5 15.9722 2.74306 15.3819 3.22917 14.8958C3.71528 14.4097 4.30556 14.1667 5 14.1667C5.19444 14.1667 5.375 14.1875 5.54167 14.2292C5.70833 14.2708 5.86806 14.3264 6.02083 14.3958L7.20833 12.9167C6.81944 12.4861 6.54861 12 6.39583 11.4583C6.24306 10.9167 6.20833 10.375 6.29167 9.83333L4.60417 9.27083C4.36806 9.61806 4.06944 9.89583 3.70833 10.1042C3.34722 10.3125 2.94444 10.4167 2.5 10.4167C1.80556 10.4167 1.21528 10.1736 0.729167 9.6875C0.243056 9.20139 0 8.61111 0 7.91667C0 7.22222 0.243056 6.63194 0.729167 6.14583C1.21528 5.65972 1.80556 5.41667 2.5 5.41667C3.19444 5.41667 3.78472 5.65972 4.27083 6.14583C4.75694 6.63194 5 7.22222 5 7.91667V8.08333L6.6875 8.66667C6.96528 8.16667 7.33694 7.74306 7.8025 7.39583C8.26805 7.04861 8.79222 6.82639 9.375 6.72917V4.91667C8.83333 4.76389 8.38556 4.46861 8.03167 4.03083C7.67778 3.59306 7.50056 3.08278 7.5 2.5C7.5 1.80556 7.74306 1.21528 8.22917 0.729167C8.71528 0.243056 9.30556 0 10 0C10.6944 0 11.2847 0.243056 11.7708 0.729167C12.2569 1.21528 12.5 1.80556 12.5 2.5C12.5 3.08333 12.3194 3.59389 11.9583 4.03167C11.5972 4.46944 11.1528 4.76444 10.625 4.91667V6.72917C11.2083 6.82639 11.7328 7.04861 12.1983 7.39583C12.6639 7.74306 13.0353 8.16667 13.3125 8.66667L15 8.08333V7.91667C15 7.22222 15.2431 6.63194 15.7292 6.14583C16.2153 5.65972 16.8056 5.41667 17.5 5.41667C18.1944 5.41667 18.7847 5.65972 19.2708 6.14583C19.7569 6.63194 20 7.22222 20 7.91667C20 8.61111 19.7569 9.20139 19.2708 9.6875C18.7847 10.1736 18.1944 10.4167 17.5 10.4167C17.0556 10.4167 16.6492 10.3125 16.2808 10.1042C15.9125 9.89583 15.6175 9.61806 15.3958 9.27083L13.7083 9.83333C13.7917 10.375 13.7569 10.9133 13.6042 11.4483C13.4514 11.9833 13.1806 12.4728 12.7917 12.9167L13.9792 14.375C14.1319 14.3056 14.2917 14.2536 14.4583 14.2192C14.625 14.1847 14.8056 14.1672 15 14.1667C15.6944 14.1667 16.2847 14.4097 16.7708 14.8958C17.2569 15.3819 17.5 15.9722 17.5 16.6667C17.5 17.3611 17.2569 17.9514 16.7708 18.4375C16.2847 18.9236 15.6944 19.1667 15 19.1667C14.3056 19.1667 13.7153 18.9236 13.2292 18.4375C12.7431 17.9514 12.5 17.3611 12.5 16.6667C12.5 16.3889 12.5453 16.1217 12.6358 15.865C12.7264 15.6083 12.8478 15.3756 13 15.1667L11.8125 13.6875C11.2431 14.0069 10.6356 14.1667 9.99 14.1667C9.34444 14.1667 8.73667 14.0069 8.16667 13.6875L7 15.1667C7.15278 15.375 7.27444 15.6078 7.365 15.865C7.45556 16.1222 7.50056 16.3894 7.5 16.6667C7.5 17.3611 7.25694 17.9514 6.77083 18.4375C6.28472 18.9236 5.69444 19.1667 5 19.1667Z"
              />
            </svg>
            Hubs
          </div>
          <router-link
            to="/pricing"
            class="flex items-center gap-3 px-4 py-2.5 text-sm font-medium text-foreground hover:bg-muted transition-colors"
            @click="showLogoDropdown = false"
          >
            <Gem width="18" height="18" class="mr-0.5" />
            Pricing
          </router-link>
        </div>
      </div>

      <!-- Center: Navbar (Only on Home) -->
      <nav
        v-if="!showBreadcrumbs"
        class="hidden xl:flex bg-card border border-border rounded-[24px] p-1 space-x-2"
      >
        <router-link to="/home" v-slot="{ isActive }" custom>
          <a
            @click="$router.push('/home')"
            :class="[
              'inline-flex items-center px-4 py-2 rounded-full text-sm font-medium transition-colors cursor-pointer group',
              isActive || $route.path === '/home'
                ? 'bg-primary text-primary-foreground dark:bg-background dark:text-foreground'
                : 'text-muted-foreground hover:bg-primary hover:text-primary-foreground dark:hover:bg-secondary dark:hover:text-secondary-foreground',
            ]"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              xmlns="http://www.w3.org/2000/svg"
              class="mr-2 fill-current"
            >
              <path
                fill="currentColor"
                d="M16.7001 5.68373L11.9001 2.3254C10.5918 1.40873 8.58345 1.45873 7.32511 2.43373L3.15011 5.69206C2.31678 6.34206 1.65845 7.6754 1.65845 8.7254V14.4754C1.65845 16.6004 3.38345 18.3337 5.50845 18.3337H14.4918C16.6168 18.3337 18.3418 16.6087 18.3418 14.4837V8.83373C18.3418 7.70873 17.6168 6.3254 16.7001 5.68373ZM10.6251 15.0004C10.6251 15.3421 10.3418 15.6254 10.0001 15.6254C9.65845 15.6254 9.37511 15.3421 9.37511 15.0004V12.5004C9.37511 12.1587 9.65845 11.8754 10.0001 11.8754C10.3418 11.8754 10.6251 12.1587 10.6251 12.5004V15.0004Z"
              />
            </svg>
            Home
          </a>
        </router-link>

        <router-link to="/learning-goals" v-slot="{ isActive }" custom>
          <a
            @click="$router.push('/learning-goals')"
            :class="[
              'inline-flex items-center px-4 py-2 rounded-full text-sm font-medium transition-colors cursor-pointer group',
              isActive
                ? 'bg-primary text-primary-foreground dark:bg-background dark:text-foreground'
                : 'text-muted-foreground hover:bg-primary hover:text-primary-foreground dark:hover:bg-secondary dark:hover:text-secondary-foreground',
            ]"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              xmlns="http://www.w3.org/2000/svg"
              class="mr-2 fill-current"
            >
              <path
                d="M18.3333 4.04173V13.9501C18.3333 14.7584 17.675 15.5001 16.8667 15.6001L16.6083 15.6334C15.2417 15.8167 13.3167 16.3834 11.7667 17.0334C11.225 17.2584 10.625 16.8501 10.625 16.2584V4.66673C10.625 4.3584 10.8 4.07507 11.075 3.92507C12.6 3.10007 14.9083 2.36673 16.475 2.2334H16.525C17.525 2.2334 18.3333 3.04173 18.3333 4.04173Z"
              />
              <path
                d="M8.92682 3.92507C7.40182 3.10007 5.09349 2.36673 3.52682 2.2334H3.46849C2.46849 2.2334 1.66016 3.04173 1.66016 4.04173V13.9501C1.66016 14.7584 2.31849 15.5001 3.12682 15.6001L3.38516 15.6334C4.75182 15.8167 6.67682 16.3834 8.22682 17.0334C8.76849 17.2584 9.36849 16.8501 9.36849 16.2584V4.66673C9.36849 4.35006 9.20182 4.07507 8.92682 3.92507ZM4.16849 6.45006H6.04349C6.38516 6.45006 6.66849 6.7334 6.66849 7.07506C6.66849 7.42506 6.38516 7.70006 6.04349 7.70006H4.16849C3.82682 7.70006 3.54349 7.42506 3.54349 7.07506C3.54349 6.7334 3.82682 6.45006 4.16849 6.45006ZM6.66849 10.2001H4.16849C3.82682 10.2001 3.54349 9.92506 3.54349 9.57506C3.54349 9.2334 3.82682 8.95006 4.16849 8.95006H6.66849C7.01016 8.95006 7.29349 9.2334 7.29349 9.57506C7.29349 9.92506 7.01016 10.2001 6.66849 10.2001Z"
              />
            </svg>
            Skill Path
          </a>
        </router-link>
        <span
          class="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium text-muted-foreground opacity-50 grayscale cursor-not-allowed select-none group"
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            xmlns="http://www.w3.org/2000/svg"
            class="mr-2 fill-current"
          >
            <path
              d="M5 19.1667C4.30556 19.1667 3.71528 18.9236 3.22917 18.4375C2.74306 17.9514 2.5 17.3611 2.5 16.6667C2.5 15.9722 2.74306 15.3819 3.22917 14.8958C3.71528 14.4097 4.30556 14.1667 5 14.1667C5.19444 14.1667 5.375 14.1875 5.54167 14.2292C5.70833 14.2708 5.86806 14.3264 6.02083 14.3958L7.20833 12.9167C6.81944 12.4861 6.54861 12 6.39583 11.4583C6.24306 10.9167 6.20833 10.375 6.29167 9.83333L4.60417 9.27083C4.36806 9.61806 4.06944 9.89583 3.70833 10.1042C3.34722 10.3125 2.94444 10.4167 2.5 10.4167C1.80556 10.4167 1.21528 10.1736 0.729167 9.6875C0.243056 9.20139 0 8.61111 0 7.91667C0 7.22222 0.243056 6.63194 0.729167 6.14583C1.21528 5.65972 1.80556 5.41667 2.5 5.41667C3.19444 5.41667 3.78472 5.65972 4.27083 6.14583C4.75694 6.63194 5 7.22222 5 7.91667V8.08333L6.6875 8.66667C6.96528 8.16667 7.33694 7.74306 7.8025 7.39583C8.26805 7.04861 8.79222 6.82639 9.375 6.72917V4.91667C8.83333 4.76389 8.38556 4.46861 8.03167 4.03083C7.67778 3.59306 7.50056 3.08278 7.5 2.5C7.5 1.80556 7.74306 1.21528 8.22917 0.729167C8.71528 0.243056 9.30556 0 10 0C10.6944 0 11.2847 0.243056 11.7708 0.729167C12.2569 1.21528 12.5 1.80556 12.5 2.5C12.5 3.08333 12.3194 3.59389 11.9583 4.03167C11.5972 4.46944 11.1528 4.76444 10.625 4.91667V6.72917C11.2083 6.82639 11.7328 7.04861 12.1983 7.39583C12.6639 7.74306 13.0353 8.16667 13.3125 8.66667L15 8.08333V7.91667C15 7.22222 15.2431 6.63194 15.7292 6.14583C16.2153 5.65972 16.8056 5.41667 17.5 5.41667C18.1944 5.41667 18.7847 5.65972 19.2708 6.14583C19.7569 6.63194 20 7.22222 20 7.91667C20 8.61111 19.7569 9.20139 19.2708 9.6875C18.7847 10.1736 18.1944 10.4167 17.5 10.4167C17.0556 10.4167 16.6492 10.3125 16.2808 10.1042C15.9125 9.89583 15.6175 9.61806 15.3958 9.27083L13.7083 9.83333C13.7917 10.375 13.7569 10.9133 13.6042 11.4483C13.4514 11.9833 13.1806 12.4728 12.7917 12.9167L13.9792 14.375C14.1319 14.3056 14.2917 14.2536 14.4583 14.2192C14.625 14.1847 14.8056 14.1672 15 14.1667C15.6944 14.1667 16.2847 14.4097 16.7708 14.8958C17.2569 15.3819 17.5 15.9722 17.5 16.6667C17.5 17.3611 17.2569 17.9514 16.7708 18.4375C16.2847 18.9236 15.6944 19.1667 15 19.1667C14.3056 19.1667 13.7153 18.9236 13.2292 18.4375C12.7431 17.9514 12.5 17.3611 12.5 16.6667C12.5 16.3889 12.5453 16.1217 12.6358 15.865C12.7264 15.6083 12.8478 15.3756 13 15.1667L11.8125 13.6875C11.2431 14.0069 10.6356 14.1667 9.99 14.1667C9.34444 14.1667 8.73667 14.0069 8.16667 13.6875L7 15.1667C7.15278 15.375 7.27444 15.6078 7.365 15.865C7.45556 16.1222 7.50056 16.3894 7.5 16.6667C7.5 17.3611 7.25694 17.9514 6.77083 18.4375C6.28472 18.9236 5.69444 19.1667 5 19.1667Z"
            />
          </svg>
          Hubs
        </span>

        <router-link to="/feed" v-slot="{ isActive }" custom>
          <a
            @click="$router.push('/feed')"
            :class="[
              'inline-flex items-center px-4 py-2 rounded-full text-sm font-medium transition-colors cursor-pointer group',
              isActive
                ? 'bg-primary text-primary-foreground dark:bg-background dark:text-foreground'
                : 'text-muted-foreground hover:bg-primary hover:text-primary-foreground dark:hover:bg-secondary dark:hover:text-secondary-foreground',
            ]"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              xmlns="http://www.w3.org/2000/svg"
              class="mr-2 fill-current"
            >
              <path
                d="M14.6089 6.47533C14.5505 6.46699 14.4922 6.46699 14.4339 6.47533C13.1422 6.43366 12.1172 5.37533 12.1172 4.07533C12.1172 2.75033 13.1922 1.66699 14.5255 1.66699C15.8505 1.66699 16.9339 2.74199 16.9339 4.07533C16.9255 5.37533 15.9005 6.43366 14.6089 6.47533Z"
              />
              <path
                d="M17.3237 12.2503C16.3904 12.8753 15.082 13.1087 13.8737 12.9503C14.1904 12.267 14.357 11.5087 14.3654 10.7087C14.3654 9.87534 14.182 9.08367 13.832 8.392C15.0654 8.22534 16.3737 8.45867 17.3154 9.08367C18.632 9.95034 18.632 11.3753 17.3237 12.2503Z"
              />
              <path
                d="M5.36745 6.47533C5.42578 6.46699 5.48411 6.46699 5.54245 6.47533C6.83411 6.43366 7.85912 5.37533 7.85912 4.07533C7.85912 2.74199 6.78411 1.66699 5.45078 1.66699C4.12578 1.66699 3.05078 2.74199 3.05078 4.07533C3.05078 5.37533 4.07578 6.43366 5.36745 6.47533Z"
              />
              <path
                d="M5.4599 10.7079C5.4599 11.5162 5.6349 12.2829 5.95156 12.9745C4.77656 13.0995 3.55156 12.8495 2.65156 12.2579C1.3349 11.3829 1.3349 9.95785 2.65156 9.08285C3.54323 8.48285 4.80156 8.24118 5.9849 8.37452C5.64323 9.07452 5.4599 9.86618 5.4599 10.7079Z"
              />
              <path
                d="M10.1 13.225C10.0333 13.2167 9.95833 13.2167 9.88333 13.225C8.35 13.175 7.125 11.9167 7.125 10.3667C7.13333 8.78333 8.40833 7.5 10 7.5C11.5833 7.5 12.8667 8.78333 12.8667 10.3667C12.8583 11.9167 11.6417 13.175 10.1 13.225Z"
              />
              <path
                d="M7.39297 14.9502C6.13464 15.7919 6.13464 17.1752 7.39297 18.0085C8.8263 18.9669 11.1763 18.9669 12.6096 18.0085C13.868 17.1669 13.868 15.7835 12.6096 14.9502C11.1846 13.9919 8.83464 13.9919 7.39297 14.9502Z"
              />
            </svg>
            Feed
          </a>
        </router-link>

        <router-link to="/pricing" v-slot="{ isActive }" custom>
          <a
            @click="$router.push('/pricing')"
            :class="[
              'inline-flex items-center px-4 py-2 rounded-full text-sm font-medium transition-colors cursor-pointer group',
              isActive
                ? 'bg-primary text-primary-foreground dark:bg-background dark:text-foreground'
                : 'text-muted-foreground hover:bg-primary hover:text-primary-foreground dark:hover:bg-secondary dark:hover:text-secondary-foreground',
            ]"
          >
            <Gem class="w-4 h-4 mr-2" />
            Pricing
          </a>
        </router-link>
      </nav>

      <!-- Right Side Actions (Right Pill) -->
      <div
        class="flex items-center gap-1 sm:gap-2 bg-muted rounded-full p-1 sm:p-1.5 sm:pr-2 sm:pl-4 px-1"
      >
        <!-- Search -->
        <div class="relative hidden xl:block">
          <input
            type="text"
            placeholder="| Search community..."
            style="border-radius: 20px"
            class="w-40 pl-8 pr-4 py-1.5 bg-background border-none text-foreground focus:outline-none focus:ring-1 focus:ring-blue-500 text-[12px]"
            v-model="searchQuery"
            @input="handleSearch"
          />
          <svg
            width="13"
            height="13"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            class="absolute left-3 top-2"
          >
            <path
              d="M7.66536 13.9997C11.1632 13.9997 13.9987 11.1641 13.9987 7.66634C13.9987 4.16854 11.1632 1.33301 7.66536 1.33301C4.16756 1.33301 1.33203 4.16854 1.33203 7.66634C1.33203 11.1641 4.16756 13.9997 7.66536 13.9997Z"
              stroke="#ABABB3"
              stroke-width="1.2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              d="M14.6654 14.6663L13.332 13.333"
              stroke="#ABABB3"
              stroke-width="1.2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </div>

        <!-- Streak -->
        <!-- <div
          class="flex items-center gap-1.5 px-3 py-1.5 bg-white dark:bg-gray-800 rounded-full"
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M8 16C11.314 16 14 14 14 10.5C14 9 13.5 6.5 11.5 4.5C11.75 6 10.25 6.5 10.25 6.5C11 4 9 0.5 6 0C6.357 2 6.5 4 4 6C2.75 7 2 8.729 2 10.5C2 14 4.686 16 8 16ZM8 15C6.343 15 5 14 5 12.25C5 11.5 5.25 10.25 6.25 9.25C6.125 10 7 10.5 7 10.5C6.625 9.25 7.5 7.25 9 7C8.821 8 8.75 9 10 10C10.625 10.5 11 11.364 11 12.25C11 14 9.657 15 8 15Z"
              fill="#FF2F09"
            />
          </svg>
          <span class="text-[12px] font-bold text-gray-900 dark:text-gray-100"
            >78</span
          >
        </div> -->

        <!-- <button
          class="p-2 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors rounded-full"
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 18 18"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M16.5 6C16.5 3 15 1.5 12 1.5H6C3 1.5 1.5 3 1.5 6V15.75C1.5 16.1625 1.8375 16.5 2.25 16.5H12C15 16.5 16.5 15 16.5 12V9"
              stroke="#667085"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              d="M11.25 7.125H12.75"
              stroke="#667085"
              stroke-width="1.5"
              stroke-miterlimit="10"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              d="M5.25391 7.125H9.00391"
              stroke="#667085"
              stroke-width="1.5"
              stroke-miterlimit="10"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              d="M5.25391 10.876H10.5039"
              stroke="#667085"
              stroke-width="1.5"
              stroke-miterlimit="10"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </button> -->

        <!-- <button
          @click="toggleNotifications"
          class="relative p-2 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors rounded-full"
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M10.0175 2.4248C7.25914 2.4248 5.01747 4.66647 5.01747 7.4248V9.83314C5.01747 10.3415 4.80081 11.1165 4.54247 11.5498L3.58414 13.1415C2.99247 14.1248 3.40081 15.2165 4.48414 15.5831C8.07581 16.7831 11.9508 16.7831 15.5425 15.5831C16.5508 15.2498 16.9925 14.0581 16.4425 13.1415L15.4841 11.5498C15.2341 11.1165 15.0175 10.3415 15.0175 9.83314V7.4248C15.0175 4.6748 12.7675 2.4248 10.0175 2.4248Z"
              stroke="#667085"
              stroke-width="1.2"
              stroke-miterlimit="10"
              stroke-linecap="round"
            />
            <path
              d="M11.5599 2.66621C11.3016 2.59121 11.0349 2.53288 10.7599 2.49954C9.9599 2.39954 9.19323 2.45788 8.47656 2.66621C8.71823 2.04954 9.31823 1.61621 10.0182 1.61621C10.7182 1.61621 11.3182 2.04954 11.5599 2.66621Z"
              fill="#667085"
              stroke="#667085"
              stroke-width="1.2"
              stroke-miterlimit="10"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              d="M12.5156 15.8838C12.5156 17.2588 11.3906 18.3838 10.0156 18.3838C9.33229 18.3838 8.69896 18.1005 8.24896 17.6505C7.79896 17.2005 7.51562 16.5671 7.51562 15.8838"
              stroke="#667085"
              stroke-width="1.2"
              stroke-miterlimit="10"
            />
          </svg>
          <span
            v-if="unreadNotifications > 0"
            class="absolute top-1.5 right-1.5 w-2 h-2 bg-red-600 rounded-full"
          ></span>
        </button> -->

        <button
          @click="toggleDarkMode"
          class="p-2 bg-card border border-border hover:bg-muted dark:bg-card dark:hover:bg-muted rounded-full transition-colors"
        >
          <Sun v-if="isDarkMode" :size="18" class="text-foreground" />
          <Moon v-else :size="18" class="text-muted-foreground" />
        </button>

        <div class="relative">
          <button
            @click="toggleProfileMenu"
            class="flex items-center p-0.5 hover:ring-2 ring-primary transition-all rounded-full"
          >
            <!-- <User
              v-if="isDarkMode"
              :size="18"
              class="text-foreground"
            /> -->
            <img
              :src="userAvatar"
              alt="User Avatar"
              class="w-8 h-8 rounded-full object-cover"
            />
          </button>
          <div
            v-if="showProfileMenu"
            class="absolute right-0 mt-3 w-64 bg-card rounded-xl shadow-xl border border-border z-50 overflow-hidden transform transition-all"
          >
            <!-- Header Section with Plan -->
            <div class="px-4 py-4 bg-muted/30 border-b border-border">
              <div class="flex items-center gap-3">
                <img
                  :src="userAvatar"
                  alt="User Avatar"
                  class="w-10 h-10 rounded-full object-cover border-2 border-primary"
                />
                <div class="flex-1 min-w-0">
                  <p class="text-sm font-semibold text-foreground truncate">
                    {{ userName }}
                  </p>
                  <router-link
                    v-if="currentPlan === 'Free Plan' || currentPlan === 'Free'"
                    to="/pricing"
                    class="text-[11px] font-medium px-2 py-0.5 bg-primary text-primary-foreground dark:bg-background dark:text-foreground border border-border rounded-full inline-block mt-1 hover:bg-primary/90 dark:hover:bg-muted/80 transition-colors cursor-pointer"
                    @click="showProfileMenu = false"
                  >
                    {{ currentPlan }}
                  </router-link>
                </div>
              </div>
            </div>

            <!-- Menu Items -->
            <div class="py-1">
              <router-link
                to="/profile"
                class="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground hover:bg-muted transition-colors"
                @click="showProfileMenu = false"
              >
                <User :size="18" class="text-muted-foreground" />
                Profile
              </router-link>

              <!-- Credits Tab -->
              <router-link
                to="/credits"
                class="flex items-center justify-between px-4 py-2.5 text-sm text-foreground hover:bg-muted transition-colors cursor-pointer"
                @click="showProfileMenu = false"
              >
                <div class="flex items-center gap-3">
                  <Gem :size="18" class="text-primary" />
                  <span>Credits</span>
                </div>
                <span
                  class="text-[11px] font-bold px-2 py-0.5 bg-muted rounded-md text-muted-foreground"
                >
                  {{ userCredits || 0 }}
                </span>
              </router-link>
              <!-- My Hubs tab -->
              <router-link
                v-if="$store.getters.isManager"
                to="/my-hubs"
                class="flex items-center justify-between px-4 py-2.5 text-sm text-foreground hover:bg-muted transition-colors cursor-pointer"
                @click="showProfileMenu = false"
              >
                <div class="flex items-center gap-3">
                  <img src="/hubs.svg" alt="My Hubs" class="w-4 h-4" />
                  <span>My Hubs</span>
                </div>
              </router-link>
              <!-- Mentorship tab -->
              <router-link
                v-if="$store.getters.isTalent || $store.getters.isManager"
                to="/mentorship"
                class="flex items-center justify-between px-4 py-2.5 text-sm text-foreground hover:bg-muted transition-colors cursor-pointer"
                @click="showProfileMenu = false"
              >
                <div class="flex items-center gap-3">
                  <User :size="18" class="text-muted-foreground" />
                  <span>Mentorship</span>
                </div>
              </router-link>

              <router-link
                to="/settings"
                class="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground hover:bg-muted transition-colors border-b border-border"
                @click="showProfileMenu = false"
              >
                <Settings :size="18" class="text-muted-foreground" />
                Settings
              </router-link>

              <button
                @click="logout"
                class="flex items-center gap-3 w-full text-left px-4 py-2.5 text-sm text-red-600 hover:bg-muted transition-colors"
              >
                <LogOut :size="18" />
                Logout
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </header>
</template>

<script>
import apiClient from "@/axios";
import { Gem, Sun, Moon, User, Settings, LogOut } from "lucide-vue-next";

export default {
  name: "HeaderNav",
  components: {
    Gem,
    Sun,
    Moon,
    User,
    Settings,
    LogOut,
  },
  data() {
    return {
      searchQuery: "",
      showProfileMenu: false,
      showLogoDropdown: false,
      unreadNotifications: 0,
      isDarkMode: false,
      userSubscription: null,
    };
  },
  created() {
    if (
      localStorage.theme === "dark" ||
      (!("theme" in localStorage) &&
        window.matchMedia("(prefers-color-scheme: dark)").matches)
    ) {
      this.isDarkMode = true;
      document.documentElement.classList.add("dark");
    } else {
      this.isDarkMode = false;
      document.documentElement.classList.remove("dark");
    }
  },
  computed: {
    showBreadcrumbs() {
      const path = this.$route.path;
      return (
        path !== "/home" &&
        path !== "/login" &&
        path !== "/signup" &&
        path !== "/"
      );
    },
    currentCategory() {
      const path = this.$route.path;
      if (path.includes("/learning-goals")) {
        return {
          label: "Learnings",
          path: "/learning-goals",
          type: "learnings",
        };
      }
      if (path.includes("/hubs")) {
        return { label: "Hubs", path: "/hubs", type: "hubs" };
      }
      if (path.includes("/feed")) {
        return { label: "Feed", path: "/feed", type: "feed" };
      }
      if (path.includes("/jobs")) {
        return { label: "Jobs", path: "/jobs", type: "jobs" };
      }
      if (path.includes("/pricing")) {
        return { label: "Pricing", path: "/pricing", type: "pricing" };
      }
      return { label: "Home", path: "/home", type: "default" };
    },
    hasSubpage() {
      const path = this.$route.path;
      // Main category pages don't show a subpage title in breadcrumbs
      if (["/learning-goals", "/hubs", "/feed", "/pricing"].includes(path))
        return false;
      return true;
    },
    currentBreadcrumbTitle() {
      const path = this.$route.path;
      const name = this.$route.name;

      if (path.includes("/learning-goals/")) {
        return this.$store.state.currentGoalTitle || "Path Details";
      }

      switch (name) {
        case "LearningGoals":
          return "All Learning Goals";
        case "LearningGoalsAll":
          return "All Learning Goals";
        case "Checkout":
          return "Checkout";
        case "Feed":
          return "Feed";
        case "Hubs":
          return "Hubs";
        case "Pricing":
          return "Pricing";
        case "Jobs":
          return "Jobs";
        case "JobDetails":
          return "Job Details";
        default:
          return name || "View Page";
      }
    },
    userAvatar() {
      const user = this.$store.state.user;
      return (
        user?.profile_picture ||
        require("@/assets/users/default-profile-picture.jpg")
      );
    },
    userName() {
      const user = this.$store.state.user;
      if (!user) return "Guest User";
      return (
        `${user.first_name || ""} ${user.last_name || ""}`.trim() ||
        user.username ||
        "User"
      );
    },
    currentPlan() {
      if (!this.userSubscription) {
        return "Free Plan";
      }

      // Return the plan name from subscription data
      return this.userSubscription.subscription_name;
    },
    userCredits() {
      if (!this.userSubscription) {
        return;
      }
      return this.userSubscription?.current_credits || 0;
    },
  },
  methods: {
    handleSearch() {
      console.log("Searching for:", this.searchQuery);
    },
    toggleNotifications() {
      this.$router.push("/notifications");
    },
    toggleProfileMenu() {
      this.showProfileMenu = !this.showProfileMenu;
    },
    toggleLogoDropdown() {
      this.showLogoDropdown = !this.showLogoDropdown;
    },
    toggleDarkMode() {
      this.isDarkMode = !this.isDarkMode;
      if (this.isDarkMode) {
        document.documentElement.classList.add("dark");
        localStorage.setItem("theme", "dark");
      } else {
        document.documentElement.classList.remove("dark");
        localStorage.setItem("theme", "light");
      }
    },
    logout() {
      this.$store.dispatch("logout");
      this.$router.push("/login");
    },
    async fetchUserSubscription() {
      try {
        const response = await apiClient.get(
          "/web/subscriptions/my-subscription",
        );
        this.userSubscription = response.data.data || null;
      } catch (error) {
        console.error("Error fetching user subscription:", error);
        // If there's an error (like 404), user likely has no active subscription
        this.userSubscription = null;
      }
    },
  },
  mounted() {
    this.fetchUserSubscription();

    document.addEventListener("click", (e) => {
      if (!this.$el.contains(e.target)) {
        this.showProfileMenu = false;
        this.showLogoDropdown = false;
      }
    });
  },
};
</script>
